import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bold, FileText, History, List, Pencil, Plus, Zap } from 'lucide-react';

import { api } from '../api/client';
import type { EmailTemplate, EmailTrigger, HardshipFilter, MaxDaysMode } from '../api/types';
import { Field } from '../components/Field';
import { Loading } from '../components/Loading';
import { useToast } from '../components/Toast';
import { pastedHtmlToTemplate, templateToHtml } from '../emailFormat';
import { formatDateTime } from '../format';

const TRIGGER_LABELS: Record<EmailTrigger, string> = {
  on_create: 'When a complaint is lodged',
  on_status_change: 'When status changes',
  on_close: 'When a complaint is closed',
  on_acknowledge: 'When receipt is acknowledged',
  on_delay: 'Automatically, once overdue (daily check)',
};

const HARDSHIP_FILTER_LABELS: Record<HardshipFilter, string> = {
  any: 'Any complaint',
  hardship_only: 'Hardship complaints only (issue codes 31–36)',
  non_hardship: 'Non-Hardship complaints only',
};

const MAX_DAYS_MODE_LABELS: Record<MaxDaysMode, string> = {
  none: 'No cap',
  fixed: 'A fixed number of days',
};

/** A short, human-readable summary of a rule's day-range and Hardship conditions for the list view. */
function describeConditions(rule: {
  hardship_filter: HardshipFilter;
  min_days_after_received: number | null;
  max_days_mode: MaxDaysMode;
  max_days_after_received: number | null;
  outcome_codes: number[];
}): string | null {
  const parts: string[] = [];
  if (rule.hardship_filter !== 'any') parts.push(HARDSHIP_FILTER_LABELS[rule.hardship_filter]);
  if (rule.outcome_codes.length > 0) parts.push(`outcome ${rule.outcome_codes.join(', ')}`);
  if (rule.min_days_after_received !== null) parts.push(`more than ${rule.min_days_after_received} days since received`);
  if (rule.max_days_mode === 'fixed') parts.push(`within ${rule.max_days_after_received} days since received`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Merge fields a template/rule body or recipient may reference — kept in sync with services/email.ts. */
const EMAIL_VARS = [
  { key: '{{reference}}', label: 'Complaint ID' },
  { key: '{{first_name}}', label: 'First name' },
  { key: '{{last_name}}', label: 'Last name' },
  { key: '{{full_name}}', label: 'Full name' },
  { key: '{{complainant_email}}', label: 'Complainant email' },
  { key: '{{complainant_phone}}', label: 'Complainant phone' },
  { key: '{{status}}', label: 'Status' },
  { key: '{{date_received}}', label: 'Date received' },
  { key: '{{date_closed}}', label: 'Date closed' },
  { key: '{{channel}}', label: 'Channel' },
  { key: '{{application_number}}', label: 'Application number' },
  { key: '{{loan_number}}', label: 'Loan number' },
  { key: '{{lodged_by}}', label: 'Lodged by' },
  { key: '{{description}}', label: 'Complaint description' },
  { key: '{{desired_outcome}}', label: 'Desired outcome' },
  { key: '{{idr_response_due_at}}', label: 'IDR due date' },
  { key: '{{material_findings}}', label: 'Material findings & facts' },
  { key: '{{decision_remediation}}', label: 'Decision & remediation' },
];

function TemplateEditor({
  template,
  onSaved,
  onCancel,
  onError,
}: {
  template?: EmailTemplate;
  onSaved: () => void;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [defaultTo, setDefaultTo] = useState(template?.default_to ?? '');
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const save = useMutation({
    mutationFn: () =>
      template
        ? api.updateEmailTemplate(template.id, { name, subject, body, default_to: defaultTo || null })
        : api.createEmailTemplate({ name, subject, body, default_to: defaultTo || undefined }),
    onSuccess: onSaved,
    onError: (err: unknown) => onError(err instanceof Error ? err.message : 'Could not save template'),
  });

  /** Replaces the current selection with `text`, or appends if the textarea isn't mounted. */
  function insertAtCursor(text: string) {
    const ta = bodyRef.current;
    if (!ta) {
      setBody((b) => b + text);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = body.slice(0, start) + text + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + text.length;
    });
  }

  function makeBold() {
    const ta = bodyRef.current;
    const selected = ta ? body.slice(ta.selectionStart, ta.selectionEnd) : '';
    insertAtCursor(`**${selected || 'bold text'}**`);
  }

  /** Prefixes each selected line (or the cursor's line) with "- ". */
  function makeBullets() {
    const ta = bodyRef.current;
    if (!ta) return;
    const start = body.lastIndexOf('\n', ta.selectionStart - 1) + 1;
    const endBreak = body.indexOf('\n', ta.selectionEnd);
    const end = endBreak === -1 ? body.length : endBreak;
    const lines = body
      .slice(start, end)
      .split('\n')
      .map((line) => (line.trim() === '' || /^\s*[-*•]\s/.test(line) ? line : `- ${line}`))
      .join('\n');
    setBody(body.slice(0, start) + lines + body.slice(end));
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = start;
      ta.selectionEnd = start + lines.length;
    });
  }

  function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const html = e.clipboardData.getData('text/html');
    if (!html) return;
    e.preventDefault();
    insertAtCursor(pastedHtmlToTemplate(html));
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && subject.trim() && body.trim()) save.mutate();
      }}
    >
      <div className="grid">
        <Field label="Template name" htmlFor="tpl-name" required>
          <input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="Default to (email address or {{variable}})" htmlFor="tpl-to">
          <input
            id="tpl-to"
            value={defaultTo}
            onChange={(e) => setDefaultTo(e.target.value)}
            placeholder="e.g. {{lodged_by}} or customer@example.com"
          />
        </Field>
      </div>
      <Field label="Subject" htmlFor="tpl-subject" required hint="Supports {{merge_field}} placeholders, e.g. {{reference}}">
        <input id="tpl-subject" value={subject} onChange={(e) => setSubject(e.target.value)} required />
      </Field>
      <Field
        label="Body"
        htmlFor="tpl-body"
        required
        hint="Click a variable below to insert it at the cursor. Use **text** for bold and start a line with - for a bullet; pasted bullets and bold are kept."
      >
        <div style={{ margin: '6px 0 10px' }}>
          {EMAIL_VARS.map((v) => (
            <button key={v.key} type="button" className="tag-pill" onClick={() => insertAtCursor(v.key)}>
              {v.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <button type="button" className="btn small" title="Bold" onClick={makeBold}>
            <Bold size={14} aria-hidden /> Bold
          </button>
          <button type="button" className="btn small" title="Bulleted list" onClick={makeBullets}>
            <List size={14} aria-hidden /> Bullets
          </button>
        </div>
        <textarea
          id="tpl-body"
          ref={bodyRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onPaste={onPaste}
          rows={8}
          required
        />
      </Field>
      <div style={{ marginTop: 12 }}>
        <h3 className="section-title" style={{ fontSize: 12 }}>Preview</h3>
        {body ? (
          // templateToHtml escapes all text before adding its own tags — see emailFormat.ts.
          <div className="email-preview formatted" dangerouslySetInnerHTML={{ __html: templateToHtml(body) }} />
        ) : (
          <div className="email-preview">Start typing above to see a preview…</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="submit" className="btn primary" disabled={save.isPending}>
          {template ? <Pencil size={16} aria-hidden /> : <Plus size={16} aria-hidden />}{' '}
          {save.isPending ? 'Saving…' : template ? 'Save changes' : 'Add template'}
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

interface EditableRule {
  id: number;
  name: string;
  trigger_on: EmailTrigger;
  template_id: number;
  to_override: string | null;
  hardship_filter: HardshipFilter;
  min_days_after_received: number | null;
  max_days_mode: MaxDaysMode;
  max_days_after_received: number | null;
  outcome_codes: number[];
}

/** ASIC outcome codes that mean a remedy was given — i.e. the complaint was upheld, fully or in part. */
const UPHELD_OUTCOME_CODES = [1, 2, 3, 4, 5];

function RuleEditor({
  rule,
  templates,
  onSaved,
  onCancel,
  onError,
}: {
  rule?: EditableRule;
  templates: { id: number; name: string }[];
  onSaved: () => void;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState(rule?.name ?? '');
  const [triggerOn, setTriggerOn] = useState<EmailTrigger>(rule?.trigger_on ?? 'on_create');
  const [templateId, setTemplateId] = useState<number | ''>(rule?.template_id ?? '');
  const [toOverride, setToOverride] = useState(rule?.to_override ?? '');
  const [hardshipFilter, setHardshipFilter] = useState<HardshipFilter>(rule?.hardship_filter ?? 'any');
  const [minDays, setMinDays] = useState(rule?.min_days_after_received?.toString() ?? '');
  const [maxDaysMode, setMaxDaysMode] = useState<MaxDaysMode>(rule?.max_days_mode ?? 'none');
  const [maxDays, setMaxDays] = useState(rule?.max_days_after_received?.toString() ?? '');
  const [outcomeCodes, setOutcomeCodes] = useState<number[]>(rule?.outcome_codes ?? []);
  const { data: reference } = useQuery({ queryKey: ['reference'], queryFn: api.reference, staleTime: 15 * 60_000 });
  const outcomes = (reference?.outcomes ?? []).filter((o) => o.code !== 999);

  function toggleOutcome(code: number) {
    setOutcomeCodes((codes) => (codes.includes(code) ? codes.filter((c) => c !== code) : [...codes, code].sort((a, b) => a - b)));
  }

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        trigger_on: triggerOn,
        template_id: Number(templateId),
        hardship_filter: hardshipFilter,
        min_days_after_received: minDays.trim() ? Number(minDays) : null,
        max_days_mode: maxDaysMode,
        max_days_after_received: maxDaysMode === 'fixed' && maxDays.trim() ? Number(maxDays) : null,
        outcome_codes: outcomeCodes,
      };
      return rule
        ? api.updateEmailRule(rule.id, { ...payload, to_override: toOverride || null })
        : api.createEmailRule({
            ...payload,
            to_override: toOverride || undefined,
            min_days_after_received: payload.min_days_after_received ?? undefined,
            max_days_after_received: payload.max_days_after_received ?? undefined,
          });
    },
    onSuccess: onSaved,
    onError: (err: unknown) => onError(err instanceof Error ? err.message : 'Could not save rule'),
  });

  if (templates.length === 0) {
    return <p className="muted">Add a template first — a rule always fires one.</p>;
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && templateId !== '') save.mutate();
      }}
    >
      <div className="grid three">
        <Field label="Rule name" htmlFor="rule-name" required>
          <input id="rule-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="Fires" htmlFor="rule-trigger">
          <select id="rule-trigger" value={triggerOn} onChange={(e) => setTriggerOn(e.target.value as EmailTrigger)}>
            {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Template" htmlFor="rule-template">
          <select
            id="rule-template"
            value={templateId}
            onChange={(e) => setTemplateId(Number(e.target.value))}
            required
          >
            <option value="" disabled>
              Choose a template…
            </option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Override recipient" htmlFor="rule-to" hint="Optional — overrides the template's own default for this rule only">
        <input id="rule-to" value={toOverride} onChange={(e) => setToOverride(e.target.value)} placeholder="{{complainant_email}}" />
      </Field>

      <h3 className="section-title" style={{ fontSize: 12, marginTop: 16 }}>Conditions</h3>
      <p className="faint" style={{ fontSize: 11, marginBottom: 10 }}>
        Optional — leave at their defaults to fire on every occurrence of the trigger above.
      </p>
      <div className="grid three">
        <Field label="Hardship filter" htmlFor="rule-hardship">
          <select id="rule-hardship" value={hardshipFilter} onChange={(e) => setHardshipFilter(e.target.value as HardshipFilter)}>
            {Object.entries(HARDSHIP_FILTER_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Minimum days since received"
          htmlFor="rule-min-days"
          hint="Fires only once more than this many days have elapsed"
        >
          <input
            id="rule-min-days"
            type="number"
            min={0}
            value={minDays}
            onChange={(e) => setMinDays(e.target.value)}
            placeholder="e.g. 5"
          />
        </Field>
        <Field label="Maximum days cap" htmlFor="rule-max-mode">
          <select id="rule-max-mode" value={maxDaysMode} onChange={(e) => setMaxDaysMode(e.target.value as MaxDaysMode)}>
            {Object.entries(MAX_DAYS_MODE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {maxDaysMode === 'fixed' && (
        <Field label="Number of days" htmlFor="rule-max-days" required>
          <input
            id="rule-max-days"
            type="number"
            min={0}
            value={maxDays}
            onChange={(e) => setMaxDays(e.target.value)}
            placeholder="e.g. 5"
            required
          />
        </Field>
      )}

      <Field
        label="Outcome"
        htmlFor="rule-outcomes"
        hint="Fires only if any of the complaint's outcome codes is ticked. Leave all unticked for any outcome. Set the outcome before closing — rules read it at the moment of the trigger."
      >
        <div id="rule-outcomes" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
            <button type="button" className="btn small" onClick={() => setOutcomeCodes(UPHELD_OUTCOME_CODES)}>
              Upheld (remedy given, 1–5)
            </button>
            <button type="button" className="btn small" onClick={() => setOutcomeCodes([])}>
              Any outcome
            </button>
          </div>
          {outcomes.map((o) => (
            <label key={o.code} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={outcomeCodes.includes(o.code)} onChange={() => toggleOutcome(o.code)} />
              {o.code} — {o.label}
            </label>
          ))}
        </div>
      </Field>

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="submit" className="btn primary" disabled={save.isPending}>
          {rule ? <Pencil size={16} aria-hidden /> : <Plus size={16} aria-hidden />}{' '}
          {save.isPending ? 'Saving…' : rule ? 'Save changes' : 'Add rule'}
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

type Tab = 'templates' | 'rules' | 'log';

export function EmailSettings() {
  const queryClient = useQueryClient();
  const { show, element: toastElement } = useToast();
  const [tab, setTab] = useState<Tab>('templates');
  const [editingTemplate, setEditingTemplate] = useState<'list' | 'new' | number>('list');
  const [editingRule, setEditingRule] = useState<'list' | 'new' | number>('list');

  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['email', 'templates'],
    queryFn: api.listEmailTemplates,
  });
  const { data: rulesData, isLoading: rulesLoading } = useQuery({ queryKey: ['email', 'rules'], queryFn: api.listEmailRules });
  const { data: logData, isLoading: logLoading } = useQuery({ queryKey: ['email', 'log'], queryFn: api.listEmailLog });

  const refreshTemplates = () => queryClient.invalidateQueries({ queryKey: ['email', 'templates'] });
  const refreshRules = () => queryClient.invalidateQueries({ queryKey: ['email', 'rules'] });

  const toggleTemplate = useMutation({
    mutationFn: (input: { id: number; is_active: boolean }) => api.updateEmailTemplate(input.id, { is_active: input.is_active }),
    onSuccess: refreshTemplates,
    onError: (err: unknown) => show(err instanceof Error ? err.message : 'Could not update template', 'error'),
  });

  const toggleRule = useMutation({
    mutationFn: (input: { id: number; enabled: boolean }) => api.updateEmailRule(input.id, { enabled: input.enabled }),
    onSuccess: refreshRules,
    onError: (err: unknown) => show(err instanceof Error ? err.message : 'Could not update rule', 'error'),
  });

  if (templatesLoading || rulesLoading || logLoading) return <Loading message="Loading email configuration…" />;

  const templates = templatesData?.items ?? [];
  const rules = rulesData?.items ?? [];
  const log = logData?.items ?? [];

  const tabs: { id: Tab; label: string; icon: typeof FileText }[] = [
    { id: 'templates', label: 'Templates', icon: FileText },
    { id: 'rules', label: 'Rules', icon: Zap },
    { id: 'log', label: 'Send log', icon: History },
  ];

  return (
    <div className="page">
      <h1 className="page-title">Email</h1>
      <p className="page-subtitle">
        Rules fire from the server when a complaint changes state — never from a browser — so every send reflects
        what was actually saved.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`btn ${tab === t.id ? 'primary' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <t.icon size={14} aria-hidden /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'templates' && (
        <div className="card">
          {editingTemplate === 'list' ? (
            <>
              <h2 className="section-title">Email templates</h2>
              {templates.length === 0 ? (
                <p className="faint" style={{ fontSize: 12 }}>No templates yet. Create your first template below.</p>
              ) : (
                templates.map((t) => (
                  <div className="rule-card" key={t.id}>
                    <label className="toggle" title={t.is_active ? 'Deactivate' : 'Reactivate'}>
                      <input
                        type="checkbox"
                        checked={Boolean(t.is_active)}
                        disabled={toggleTemplate.isPending}
                        onChange={() => toggleTemplate.mutate({ id: t.id, is_active: !t.is_active })}
                      />
                      <span className="toggle-slider" />
                    </label>
                    <div className="rule-card-body">
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</div>
                      <div className="rule-meta">Subject: {t.subject}</div>
                      <div className="rule-meta" style={{ marginTop: 2 }}>
                        {t.body.length > 80 ? `${t.body.slice(0, 80)}…` : t.body}
                      </div>
                      <div className="rule-meta" style={{ marginTop: 2 }}>Default to: {t.default_to || 'Complainant email'}</div>
                    </div>
                    <button type="button" className="btn small" title="Edit" onClick={() => setEditingTemplate(t.id)}>
                      <Pencil size={14} aria-hidden />
                    </button>
                  </div>
                ))
              )}
              <button type="button" className="btn primary" style={{ marginTop: 12 }} onClick={() => setEditingTemplate('new')}>
                <Plus size={16} aria-hidden /> New template
              </button>
            </>
          ) : (
            <>
              <h2 className="section-title">{editingTemplate === 'new' ? 'New template' : 'Edit template'}</h2>
              <TemplateEditor
                template={editingTemplate === 'new' ? undefined : templates.find((t) => t.id === editingTemplate)}
                onSaved={() => {
                  void refreshTemplates();
                  setEditingTemplate('list');
                  show('Template saved');
                }}
                onCancel={() => setEditingTemplate('list')}
                onError={(m) => show(m, 'error')}
              />
            </>
          )}
        </div>
      )}

      {tab === 'rules' && (
        <div className="card">
          {editingRule === 'list' ? (
            <>
              <h2 className="section-title">Automation rules</h2>
              <p className="muted" style={{ fontSize: 12, marginBottom: 16 }}>
                Rules automatically send emails when complaints are created, updated, or closed, using your saved
                templates.
              </p>
              {rules.length === 0 ? (
                <p className="faint" style={{ fontSize: 12 }}>No rules yet — nothing sends automatically until one exists.</p>
              ) : (
                rules.map((r) => (
                  <div className="rule-card" key={r.id}>
                    <label className="toggle" title={r.enabled ? 'Disable' : 'Enable'}>
                      <input
                        type="checkbox"
                        checked={Boolean(r.enabled)}
                        disabled={toggleRule.isPending}
                        onChange={() => toggleRule.mutate({ id: r.id, enabled: !r.enabled })}
                      />
                      <span className="toggle-slider" />
                    </label>
                    <div className="rule-card-body">
                      <div className="rule-trigger">{TRIGGER_LABELS[r.trigger_on]}</div>
                      <div className="rule-desc">{r.name}</div>
                      <div className="rule-meta">
                        Template: {r.template_name} · To:{' '}
                        {r.to_override || templates.find((t) => t.id === r.template_id)?.default_to || '—'}
                      </div>
                      {describeConditions(r) && (
                        <div className="rule-meta" style={{ marginTop: 2 }}>Conditions: {describeConditions(r)}</div>
                      )}
                    </div>
                    <button type="button" className="btn small" title="Edit" onClick={() => setEditingRule(r.id)}>
                      <Pencil size={14} aria-hidden />
                    </button>
                  </div>
                ))
              )}
              {templates.length === 0 && (
                <p className="faint" style={{ fontSize: 12, marginTop: 8 }}>Create a template first before adding rules.</p>
              )}
              <button
                type="button"
                className="btn primary"
                style={{ marginTop: 12 }}
                disabled={templates.length === 0}
                onClick={() => setEditingRule('new')}
              >
                <Plus size={16} aria-hidden /> New rule
              </button>
            </>
          ) : (
            <>
              <h2 className="section-title">{editingRule === 'new' ? 'New rule' : 'Edit rule'}</h2>
              <RuleEditor
                rule={editingRule === 'new' ? undefined : rules.find((r) => r.id === editingRule)}
                templates={templates.map((t) => ({ id: t.id, name: t.name }))}
                onSaved={() => {
                  void refreshRules();
                  setEditingRule('list');
                  show('Rule saved');
                }}
                onCancel={() => setEditingRule('list')}
                onError={(m) => show(m, 'error')}
              />
            </>
          )}
        </div>
      )}

      {tab === 'log' && (
        <div className="card">
          <h2 className="section-title">Send log</h2>
          {log.length === 0 ? (
            <p className="faint" style={{ fontSize: 12 }}>No emails sent yet.</p>
          ) : (
            log.map((entry) => (
              <div className="send-history-row" key={entry.id}>
                <span className="faint" style={{ minWidth: 150 }}>{formatDateTime(entry.created_at)}</span>
                <span style={{ flex: 1 }}>{entry.subject}</span>
                <span className="faint" style={{ minWidth: 180 }}>{entry.to_address}</span>
                <span title={entry.error_message ?? undefined}>
                  <span className={`badge ${entry.status === 'sent' ? 'open' : 'overdue'}`}>{entry.status}</span>
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {toastElement}
    </div>
  );
}
